import { env } from '@/lib/env';
import { getCsrfToken } from './csrf';

const API_BASE = `${env.API_BASE}`;

export interface AuthUser {
  uuid: string
  email: string
  name: string | null
  picture: string | null
  roles: string | null
  /** The application-level role of the user (e.g. "ADMIN", "USER"). */
  role: string | null
}

export type SessionResult =
  | { status: 'authenticated'; user: AuthUser }
  | { status: 'unauthenticated' }
  | { status: 'unreachable' }

/**
 * Session check that distinguishes "not signed in" from "server unreachable",
 * so the app shell can show a connection-lost state instead of silently
 * bouncing an active reviewer to the marketing page.
 */
export async function getSession(): Promise<SessionResult> {
  try {
    const response = await fetch(`${API_BASE}/auth/me`, {
      credentials: 'include',
    });

    if (response.ok) {
      return { status: 'authenticated', user: await response.json() }
    }
    // A dying backend (5xx) is a connectivity problem, not a sign-out.
    if (response.status >= 500) {
      return { status: 'unreachable' }
    }
    return { status: 'unauthenticated' }
  } catch {
    return { status: 'unreachable' }
  }
}

export async function getCurrentUser(): Promise<AuthUser | null> {
  try {
    const response = await fetch(`${API_BASE}/auth/me`, {
      credentials: 'include',
    });

    if (!response.ok) {
      return null
    }

    return response.json()
  } catch {
    return null
  }
}

export async function checkSession(): Promise<boolean> {
  const user = await getCurrentUser()
  return Boolean(user)
}

export function logout(): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = `${API_BASE}/auth/logout`;
  // Logout ends the session, so it is a state change the server CSRF-protects like any other; a
  // plain form POST cannot carry a header, so the cookie's token goes in Spring's `_csrf` field.
  const token = getCsrfToken();
  if (token) {
    const csrf = document.createElement('input');
    csrf.type = 'hidden';
    csrf.name = '_csrf';
    csrf.value = token;
    form.appendChild(csrf);
  }
  document.body.appendChild(form);
  form.submit();
}

// ── Cached session for the current page load ──────────────────────────────────────────────
// The route guards (beforeLoad) need the caller's role but must not hit /auth/me on every
// navigation. This caches the session for the lifetime of the document and is shared with
// AuthProvider, so the whole app makes a single /auth/me on load. It is safe against session
// timeout precisely because every auth transition here is a full navigation that drops the cache:
// the OAuth login redirect, the logout form POST, and the global 401 handler's
// window.location = '/'. A timed-out session therefore can't be served stale — the next API call
// 401-redirects and the ensuing reload refetches.
let sessionPromise: Promise<SessionResult> | null = null;

/** The session for this page load, fetched once on first read and reused thereafter. */
export function ensureSession(): Promise<SessionResult> {
  if (!sessionPromise) {
    sessionPromise = getSession();
  }
  return sessionPromise;
}

/** Force a fresh /auth/me and replace the cache — after a sign-in or a manual retry. */
export function reloadSession(): Promise<SessionResult> {
  sessionPromise = getSession();
  return sessionPromise;
}

/** Drop the cached session on sign-out so the next read refetches. */
export function clearCachedSession(): void {
  sessionPromise = null;
}

export const authService = {
  checkSession,
  getCurrentUser,
  getSession,
  ensureSession,
  reloadSession,
  clearCachedSession,
  logout,
};
